// =============================================================================
// Origin UI — src/pages/approvals/LeaveApprovalReviewPage.tsx
// Leave request review page — accessed from Unified Approvals Inbox.
// Shows employee context, request detail, team impact, decision panel.
// HR Admin can Revoke approved requests.
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/services/apiService';
import { leaveApi, type LeaveRequestDetail, type LeaveBalance, type LeaveOverlapWarning } from '@/services/leaveApi';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader, Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import styles from './approvals.module.css';

export function LeaveApprovalReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { roles } = useAuth();
  const [request, setRequest] = useState<LeaveRequestDetail | null>(null);
  const [balance, setBalance] = useState<LeaveBalance[]>([]);
  const [overlap, setOverlap] = useState<LeaveOverlapWarning | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comments, setComments] = useState('');
  const [acting, setActing] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');

  useEffect(() => { if (id) loadData(); }, [id]);

  async function loadData() {
    try {
      setLoading(true);
      const req = await leaveApi.getRequest(id!);
      setRequest(req);

      // Load balance for the employee
      try {
        const bal = await api.get<LeaveBalance[]>(`/api/leave/team/balance/${req.employeeId}`);
        setBalance(bal);
      } catch { /* balance lookup optional */ }

      // Load team overlap for the approval context (manager's direct reports)
      try {
        const ov = await api.get<LeaveOverlapWarning>(`/api/leave/requests/${id}/team-overlap`);
        setOverlap(ov);
      } catch { /* team overlap check optional */ }
    } catch (err: any) {
      setError(err.message || 'Failed to load leave request');
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove() {
    try {
      setActing(true);
      setError('');
      await leaveApi.approveRequest(id!, comments || undefined);
      setActionMsg('Leave request approved.');
      setTimeout(() => navigate('/approvals'), 1500);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || err.message);
    } finally {
      setActing(false);
    }
  }

  async function handleReject() {
    try {
      setActing(true);
      setError('');
      await leaveApi.rejectRequest(id!, comments || undefined);
      setActionMsg('Leave request rejected.');
      setTimeout(() => navigate('/approvals'), 1500);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || err.message);
    } finally {
      setActing(false);
    }
  }

  async function handleRevoke() {
    if (!revokeReason.trim()) {
      setError('A reason is required when revoking a leave request.');
      return;
    }
    try {
      setActing(true);
      setError('');
      await leaveApi.revokeRequest(id!, revokeReason);
      setActionMsg('Leave request revoked. Days returned to employee balance.');
      setShowRevokeConfirm(false);
      setRevokeReason('');
      setTimeout(() => navigate('/approvals'), 2000);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || err.message);
    } finally {
      setActing(false);
    }
  }

  if (loading) return <LoadingSpinner message="Loading leave request..." />;
  if (!request) return <ErrorBanner message={error || 'Request not found'} />;

  const leaveBalance = balance.find(b => b.leaveTypeCode === request.leaveTypeCode);
  const isSubmitted = request.status === 'Submitted';
  const isApproved = request.status === 'Approved';

  // Available balance already deducts this request as "Pending" when status is Submitted.
  // Add the days back so "Available" shows the balance BEFORE this request,
  // and "After This Request" shows the balance IF approved.
  const adjustedAvailable = leaveBalance
    ? (isSubmitted ? leaveBalance.available + request.totalWorkingDays : leaveBalance.available)
    : 0;
  const afterRequest = adjustedAvailable - request.totalWorkingDays;
  const canRevoke = isApproved && roles.isHRAdmin;

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <a href="/approvals" style={{ fontSize: 13, color: 'var(--turquoise-dark)', fontWeight: 600, textDecoration: 'none' }}>← Back to Approvals</a>
      </div>
      <PageHeader
        title="Leave Approval Review"
        subtitle={`${request.employeeName} — ${request.leaveTypeName}`}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {actionMsg && (
        <div className={styles.successBanner}>{actionMsg}</div>
      )}

      {/* ─── Revoke Confirmation Dialog ─── */}
      {showRevokeConfirm && (
        <div className={styles.revokeOverlay} onClick={() => setShowRevokeConfirm(false)}>
          <div className={styles.revokeDialog} onClick={e => e.stopPropagation()}>
            <h3 className={styles.revokeDialogTitle}>Revoke Leave Request</h3>
            <p className={styles.revokeDialogMessage}>
              You are revoking an approved leave request for <strong>{request.employeeName}</strong>.
              The {request.totalWorkingDays} day{request.totalWorkingDays !== 1 ? 's' : ''} will be
              returned to the employee's balance.
            </p>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className={styles.decisionLabel}>Reason (required)</label>
              <textarea
                className={styles.dialogTextarea}
                rows={3}
                value={revokeReason}
                onChange={e => setRevokeReason(e.target.value)}
                placeholder="Explain why this approved leave is being revoked..."
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className={styles.btnSecondary} onClick={() => setShowRevokeConfirm(false)} disabled={acting}>
                Go Back
              </button>
              <button className={styles.btnRevoke} onClick={handleRevoke} disabled={acting || !revokeReason.trim()}>
                {acting ? 'Revoking...' : 'Confirm Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.reviewLayout}>
        {/* Main content */}
        <div className={styles.reviewMain}>
          {/* Section A: Employee Context */}
          <Card className={styles.section}>
            <h3 className={styles.sectionTitle}>Employee</h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>Name</span>
                <span className={styles.detailValue}>{request.employeeName}</span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>Employee #</span>
                <span className={styles.detailValue}>{request.employeeNumber}</span>
              </div>
              {leaveBalance && (
                <>
                  <div className={styles.detailField}>
                    <span className={styles.detailLabel}>Entitlement</span>
                    <span className={styles.detailValue}>{leaveBalance.entitlement} days</span>
                  </div>
                  <div className={styles.detailField}>
                    <span className={styles.detailLabel}>Available Balance</span>
                    <span className={styles.detailValue} style={{ fontWeight: 700, color: adjustedAvailable >= 0 ? '#00AA6E' : '#F04B41' }}>
                      {adjustedAvailable % 1 === 0 ? adjustedAvailable : adjustedAvailable.toFixed(1)} days
                    </span>
                  </div>
                  <div className={styles.detailField}>
                    <span className={styles.detailLabel}>After This Request</span>
                    <span className={styles.detailValue} style={{ fontWeight: 700, color: afterRequest >= 0 ? '#00AA6E' : '#F04B41' }}>
                      {afterRequest % 1 === 0 ? afterRequest : afterRequest.toFixed(1)} days
                    </span>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Section B: Request Detail */}
          <Card className={styles.section}>
            <h3 className={styles.sectionTitle}>Leave Request</h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>Leave Type</span>
                <span className={styles.detailValue}>{request.leaveTypeName} ({request.leaveTypeCode})</span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>Start Date</span>
                <span className={styles.detailValue}>
                  {new Date(request.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {request.startDateIsHalfDay && ` (${request.startDateHalfDayPeriod})`}
                </span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>End Date</span>
                <span className={styles.detailValue}>
                  {new Date(request.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {request.endDateIsHalfDay && ` (${request.endDateHalfDayPeriod})`}
                </span>
              </div>
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>Working Days</span>
                <span className={styles.detailValue} style={{ fontWeight: 700 }}>{request.totalWorkingDays} day{request.totalWorkingDays !== 1 ? 's' : ''}</span>
              </div>
              {request.reason && (
                <div className={styles.detailField} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.detailLabel}>Reason</span>
                  <span className={styles.detailValue}>{request.reason}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Section C: Team Availability */}
          {overlap && (
            <Card className={styles.section}>
              <h3 className={styles.sectionTitle}>Team Availability</h3>
              {overlap.teamMembersOffCount > 0 ? (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: '#E87722', fontWeight: 600 }}>
                    ⚠️ {overlap.teamMembersOffCount} team member{overlap.teamMembersOffCount !== 1 ? 's' : ''} on leave during this period ({overlap.teamMembersOffCount} of {overlap.totalTeamSize})
                  </p>
                  {overlap.peopleOff.map((p, i) => (
                    <div key={i} className={styles.overlapRow}>
                      <span style={{ fontWeight: 600 }}>{p.employeeName}</span>
                      <span className={styles.muted}>
                        {p.leaveTypeName}: {new Date(p.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – {new Date(p.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: 13, color: '#00AA6E', fontWeight: 600 }}>
                  ✅ No team members on leave during this period
                </p>
              )}
            </Card>
          )}
        </div>

        {/* Sidebar: Status + Decision Panel */}
        <div className={styles.reviewSidebar}>
          <Card>
            <div className={styles.sidebarTitle}>Status</div>
            <div className={styles.summaryMeta}>
              <div>
                <span>Status</span>
                <span style={{ fontWeight: 600, color: isSubmitted ? '#E87722' : isApproved ? '#00AA6E' : '#6B7885' }}>
                  {request.status}
                </span>
              </div>
              <div>
                <span>Submitted</span>
                <span>{new Date(request.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              </div>
              {request.approverName && (
                <div>
                  <span>Reviewed by</span>
                  <span>{request.approverName}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Decision panel: Approve/Reject for Submitted */}
          {isSubmitted && (
            <div className={styles.decisionBar} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
              <div className={styles.decisionLabel}>Your Decision</div>
              <textarea
                className={styles.dialogTextarea}
                placeholder="Comments (optional for approve, recommended for reject)"
                value={comments}
                onChange={e => setComments(e.target.value)}
                rows={3}
              />
              <div className={styles.decisionActions}>
                <button className={styles.btnApprove} onClick={handleApprove} disabled={acting}>
                  {acting ? 'Processing...' : 'Approve'}
                </button>
                <button className={styles.btnCancelAction} onClick={handleReject} disabled={acting}>
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Revoke panel: HR Admin on Approved requests */}
          {canRevoke && (
            <div className={styles.decisionBar} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
              <div className={styles.decisionLabel}>HR Actions</div>
              <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: 0, lineHeight: 1.5 }}>
                This request has been approved. As HR Admin you can revoke it if needed — the days will be returned to the employee's balance.
              </p>
              <button className={styles.btnRevoke} onClick={() => setShowRevokeConfirm(true)} disabled={acting}>
                Revoke Leave
              </button>
            </div>
          )}

          {/* Info banner for terminal statuses (non-Submitted, non-Approved, or Approved but not HR) */}
          {!isSubmitted && !canRevoke && (
            <div className={styles.infoBanner}>
              This request has already been <strong>{request.status}</strong>.
              {request.approverComments && (
                <div style={{ marginTop: 8 }}>
                  <strong>Comments:</strong> {request.approverComments}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
